import { useMemo, useState } from "react";
import { CreateShiftInput } from "@ndis/contracts";
import { api, type Participant } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import AvailabilityPanel from "../components/AvailabilityPanel";

interface Conflict { workerId: number; workerName: string; type: string; detail: string }

interface ShiftRow {
  id: number;
  startsAt: string;
  endsAt: string;
  serviceCategory: string;
  status: string;
  isSleepover: boolean;
  participants: { id: number; name: string }[];
  workers: { id: number; name: string }[];
}
interface Worker { id: number; name: string; role: string }

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const field = "input";

function mondayOf(d: Date): Date {
  const out = new Date(d);
  out.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  out.setHours(0, 0, 0, 0);
  return out;
}
const iso = (d: Date) => d.toISOString().slice(0, 10);

export default function Roster() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }),
    [weekStart],
  );
  const from = iso(days[0]!);
  const to = iso(days[6]!);

  const { data: shifts, reload } = useFetch<ShiftRow[]>(`/v1/rostering/shifts?from=${from}&to=${to}`);
  const { data: workers } = useFetch<Worker[]>("/v1/rostering/workers");
  const { data: participants } = useFetch<Participant[]>("/v1/participants");

  const [showForm, setShowForm] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [form, setForm] = useState({ date: from, start: "09:00", end: "17:00", participantId: "", workerId: "", sleepover: false });
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<Conflict[]>([]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const parsed = CreateShiftInput.safeParse({
      startsAt: `${form.date}T${form.start}`,
      endsAt: `${form.date}T${form.end}` <= `${form.date}T${form.start}` ? "" : `${form.date}T${form.end}`,
      serviceCategory: "daily_activities",
      participantIds: form.participantId ? [Number(form.participantId)] : [],
      workerIds: form.workerId ? [Number(form.workerId)] : [],
      isSleepover: form.sleepover,
    });
    if (!parsed.success) { setError("Check date, times (end after start), participant, and worker"); return; }
    try {
      const created = await api<{ warnings: Conflict[] }>("/v1/rostering/shifts", { method: "POST", body: JSON.stringify(parsed.data) });
      setShowForm(false); setError(null); setWarnings(created.warnings ?? []); reload();
    } catch (err) { setError((err as Error).message); }
  }

  function shiftsFor(day: Date): ShiftRow[] {
    const key = iso(day);
    return (shifts ?? []).filter((s) => s.startsAt.startsWith(key))
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }
  const move = (w: number) => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7 * w); return n; });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">Roster</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="btn-secondary px-3 py-1.5">←</button>
          <span className="text-sm text-body w-44 text-center">{from} → {to}</span>
          <button onClick={() => move(1)} className="btn-secondary px-3 py-1.5">→</button>
          <button onClick={() => setShowAvailability((v) => !v)} className="ml-2 btn-secondary px-3 py-1.5">
            {showAvailability ? "Hide availability" : "Availability"}
          </button>
          <button onClick={() => setShowForm((v) => !v)}
            className="btn-primary">
            {showForm ? "Cancel" : "New shift"}
          </button>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="card border border-amber-300 bg-amber-50 p-4 space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-amber-800">Shift created with {warnings.length} conflict warning{warnings.length > 1 ? "s" : ""}</p>
            <button onClick={() => setWarnings([])} className="text-sm text-amber-700 hover:underline">Dismiss</button>
          </div>
          <ul className="text-sm text-amber-800 list-disc pl-5">
            {warnings.map((w, i) => (
              <li key={i}>{w.workerName}: {w.type === "unavailable" ? "outside declared availability" : w.detail}</li>
            ))}
          </ul>
        </div>
      )}

      {showAvailability && <AvailabilityPanel workers={workers ?? []} />}

      {showForm && (
        <form onSubmit={create} className="card p-5 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
          <label className="block"><span className="label">Date</span>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={field} /></label>
          <label className="block"><span className="label">Start</span>
            <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className={field} /></label>
          <label className="block"><span className="label">End</span>
            <input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className={field} /></label>
          <label className="block"><span className="label">Participant</span>
            <select value={form.participantId} onChange={(e) => setForm({ ...form, participantId: e.target.value })} className={field}>
              <option value="">Select…</option>
              {(participants ?? []).map((p) => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
            </select></label>
          <label className="block"><span className="label">Worker</span>
            <select value={form.workerId} onChange={(e) => setForm({ ...form, workerId: e.target.value })} className={field}>
              <option value="">Select…</option>
              {(workers ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select></label>
          <div className="flex items-center gap-2 pb-2">
            <label className="flex items-center gap-2 text-sm text-body">
              <input type="checkbox" checked={form.sleepover} onChange={(e) => setForm({ ...form, sleepover: e.target.checked })} />
              Sleepover
            </label>
            <button className="btn-dark">Create</button>
          </div>
          {error && <p className="text-sm text-red-600 col-span-full">{error}</p>}
        </form>
      )}

      <div className="grid grid-cols-7 gap-2">
        {days.map((d, i) => (
          <div key={i} className="min-h-40 card rounded-xl p-2">
            <p className="text-xs font-medium text-mut mb-2">{DAY_LABELS[i]} <span className="text-faint">{d.getDate()}</span></p>
            <div className="space-y-1.5">
              {shiftsFor(d).map((s) => (
                <div key={s.id} className={`rounded-lg px-2 py-1.5 text-xs ${s.isSleepover ? "bg-indigo-50 text-indigo-800" : "bg-teal-50 text-teal-800"}`}>
                  <p className="font-medium">{s.startsAt.slice(11)}–{s.endsAt.slice(11)}{s.isSleepover ? " · sleepover" : ""}</p>
                  <p className="truncate">{s.participants.map((p) => p.name).join(", ")}</p>
                  <p className="truncate opacity-70">{s.workers.map((w) => w.name).join(", ")}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-sm text-faint">Recurring shifts, open-shift claiming, and drag-to-reschedule land in later sprints.</p>
    </div>
  );
}
